import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Award, TrendingUp, Target, Trophy, ChevronRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PerformanceThreshold, getDefaultThresholds, getPerformanceLevelFromThresholds } from "@/types/performanceThresholds";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";

interface EmployeeDashboardProps {
  employeeId: string;
  employeeName: string;
  onNavigateToEvaluations?: () => void;
}

interface RankingEntry {
  rank: number;
  displayName: string;
  score: number;
  isCurrentUser: boolean;
  employeeId: string;
}

const EmployeeDashboard = ({ employeeId, employeeName, onNavigateToEvaluations }: EmployeeDashboardProps) => {
  const [loading, setLoading] = useState(true);
  const [rankingData, setRankingData] = useState<RankingEntry[]>([]);
  const [currentUserStats, setCurrentUserStats] = useState<{
    rank: number;
    score: number;
    totalEmployees: number;
    performanceLevel: string;
    evaluationCount: number;
    latestMonth: string | null;
  } | null>(null);

  const thresholds = useMemo((): PerformanceThreshold[] => {
    const stored = localStorage.getItem('performanceThresholds');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return getDefaultThresholds();
      }
    }
    return getDefaultThresholds();
  }, []);

  useEffect(() => {
    const loadRankingData = async () => {
      setLoading(true);
      try {
        // Load all employees - using any to avoid TS2589 deep type instantiation
        const empResult = await (supabase as any)
          .from('employees')
          .select('id, name, exclude_from_evaluation')
          .is('archived_at', null)
          .eq('exclude_from_evaluation', false);

        if (empResult.error) throw empResult.error;
        
        // Map to simple objects
        const employees = (empResult.data ?? []).map((e: any) => ({
          id: String(e.id),
          name: String(e.name)
        }));

        // Load evaluations
        const evalResult = await (supabase as any)
          .from('evaluations')
          .select('employee_id, total_score, max_score, month');

        if (evalResult.error) throw evalResult.error;
        
        // Map to simple objects
        const evaluations = (evalResult.data ?? []).map((e: any) => ({
          employee_id: String(e.employee_id),
          total_score: Number(e.total_score),
          max_score: Number(e.max_score),
          month: String(e.month)
        }));

        if (employees.length === 0) {
          setLoading(false);
          return;
        }

        // Calculate average score per employee
        type EmployeeScore = { id: string; name: string; avgScore: number; evalCount: number; latestMonth: string | null };
        const employeeScores: EmployeeScore[] = [];

        employees.forEach((emp) => {
          const empEvals = evaluations.filter((e) => e.employee_id === emp.id);
          if (empEvals.length === 0) return;

          const avgScore =
            empEvals.reduce(
              (sum, e) => sum + (e.max_score > 0 ? (e.total_score / e.max_score) * 100 : 0),
              0
            ) / empEvals.length;

          const latestMonth = empEvals
            .map((e) => e.month)
            .sort()
            .reverse()[0] || null;

          employeeScores.push({
            id: emp.id,
            name: emp.name,
            avgScore,
            evalCount: empEvals.length,
            latestMonth,
          });
        });

        // Sort by score descending
        employeeScores.sort((a, b) => b.avgScore - a.avgScore);

        // Create ranking with anonymization
        const ranking: RankingEntry[] = employeeScores.map((emp, index) => ({
          rank: index + 1,
          displayName: emp.id === employeeId ? emp.name : `Platz ${index + 1}`,
          score: Math.round(emp.avgScore * 10) / 10,
          isCurrentUser: emp.id === employeeId,
          employeeId: emp.id,
        }));

        setRankingData(ranking);

        // Find current user stats
        const currentUser = employeeScores.find((e) => e.id === employeeId);
        if (currentUser) {
          const rank = employeeScores.indexOf(currentUser) + 1;
          const performanceLevel = getPerformanceLevelFromThresholds(
            currentUser.avgScore,
            100,
            thresholds
          );

          setCurrentUserStats({
            rank,
            score: Math.round(currentUser.avgScore * 10) / 10,
            totalEmployees: employeeScores.length,
            performanceLevel,
            evaluationCount: currentUser.evalCount,
            latestMonth: currentUser.latestMonth,
          });
        }
      } catch (error) {
        console.error("Error loading ranking data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadRankingData();
  }, [employeeId, thresholds]);

  const handleBarClick = (data: RankingEntry) => {
    if (data && data.isCurrentUser && onNavigateToEvaluations) {
      onNavigateToEvaluations();
    }
  };

  const handleNavigateToDetails = () => {
    if (onNavigateToEvaluations) {
      onNavigateToEvaluations();
    }
  };

  const getPerformanceLevelColor = (level: string): string => {
    const threshold = thresholds.find((t) => t.level === level);
    if (threshold) {
      return `hsl(var(--${threshold.color}))`;
    }
    return "hsl(var(--muted))";
  };

  const getRankEmoji = (rank: number) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return "";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUserStats || rankingData.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Noch keine Bewertungen</h3>
          <p className="text-muted-foreground">
            Du hast noch keine Bewertungen erhalten. Sobald Bewertungen vorliegen, siehst du hier dein Ranking.
          </p>
        </CardContent>
      </Card>
    );
  }

  const averageScore = rankingData.reduce((sum, r) => sum + r.score, 0) / rankingData.length;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Dein Ranking</CardTitle>
            <Trophy className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">
              {getRankEmoji(currentUserStats.rank)} Platz {currentUserStats.rank}
            </div>
            <p className="text-xs text-muted-foreground">
              von {currentUserStats.totalEmployees} Mitarbeitern
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Dein Score</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currentUserStats.score}%</div>
            <Progress value={currentUserStats.score} className="mt-2 h-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Leistungsniveau</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Badge
              style={{
                backgroundColor: getPerformanceLevelColor(currentUserStats.performanceLevel),
                color: "white",
              }}
              className="text-sm"
            >
              {currentUserStats.performanceLevel}
            </Badge>
            <p className="text-xs text-muted-foreground mt-2">
              Basierend auf {currentUserStats.evaluationCount} Bewertung(en)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Trend</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {currentUserStats.score > averageScore ? "↑" : currentUserStats.score < averageScore ? "↓" : "→"}
              {" "}
              {Math.abs(currentUserStats.score - averageScore).toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {currentUserStats.score > averageScore
                ? "über dem Durchschnitt"
                : currentUserStats.score < averageScore
                ? "unter dem Durchschnitt"
                : "im Durchschnitt"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Ranking Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Anonymes Mitarbeiter-Ranking
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Klicke auf deinen Namen, um zu deiner detaillierten Bewertungstabelle zu gelangen.
          </p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={Math.max(300, rankingData.length * 40)}>
            <BarChart
              data={rankingData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} unit="%" />
              <YAxis
                type="category"
                dataKey="displayName"
                width={150}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: number) => [`${value}%`, "Score"]}
                cursor={{ fill: "hsl(var(--muted) / 0.3)" }}
              />
              <ReferenceLine
                x={averageScore}
                stroke="hsl(var(--muted-foreground))"
                strokeDasharray="5 5"
                label={{
                  value: `Ø ${averageScore.toFixed(1)}%`,
                  position: "top",
                  fill: "hsl(var(--muted-foreground))",
                  fontSize: 12,
                }}
              />
              <Bar
                dataKey="score"
                onClick={(data) => handleBarClick(data)}
                cursor="pointer"
              >
                {rankingData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.isCurrentUser
                        ? "hsl(var(--primary))"
                        : "hsl(var(--muted))"
                    }
                    stroke={entry.isCurrentUser ? "hsl(var(--primary))" : "none"}
                    strokeWidth={entry.isCurrentUser ? 2 : 0}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Quick Link to Evaluation Details */}
      <Card
        className="cursor-pointer hover:bg-muted/50 transition-colors border-primary/20"
        onClick={handleNavigateToDetails}
      >
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Target className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h4 className="font-semibold">Deine detaillierte Bewertungsübersicht</h4>
              <p className="text-sm text-muted-foreground">
                Sieh dir alle deine Bewertungen und Kriterien im Detail an
              </p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeDashboard;
